import React, { useMemo, useState } from 'react';
import { Plus, Pencil, Trash, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import recordOperationsDownload from '../../utils/recordOperationsDownload';
import { SearchBar } from '../SearchBar/SearchBar';
import { Modal } from '../Modal/Modal';

export function BrandItemModule({ materials, brandItems, onAdd, onUpdate, onDelete }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newData, setNewData] = useState({ materialType: 'Material', name: '', status: 'Activo' });
  const [editData, setEditData] = useState({ materialCode: '', name: '', status: 'Activo' });
  const [errorMsg, setErrorMsg] = useState('');
  const [statusMsg, setStatusMsg] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return brandItems;
    return brandItems.filter((b) =>
      [b.materialCode, b.name, b.status]
        .some((v) => String(v ?? '').toLowerCase().includes(q))
    );
  }, [query, brandItems]);

  const openNew = () => {
    setEditing(null);
    setErrorMsg('');
    setIsOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setEditData({ materialCode: b.materialCode || '', name: b.name || '', status: b.status || 'Activo' });
    setErrorMsg('');
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setEditing(null);
    setErrorMsg('');
  };

  const handleSave = (data) => {
    const isEditExisting = editing && typeof editing.id !== 'undefined';
    const name = (data.name || '').trim();
    const status = (data.status || '').trim() || 'Activo';
    const materialType = (data.materialType || 'Material').trim();

    if (!name) { setErrorMsg('El nombre es obligatorio.'); return; }

    // Validar que no exista el mismo par materialCode + name
    const normalizedName = name.toLowerCase();
    const isDuplicate = brandItems.some(bi => (
      (bi.name || '').trim().toLowerCase() === normalizedName &&
      ((bi.materialType || '') === materialType) &&
      (!isEditExisting || bi.id !== editing.id)
    ));
    if (isDuplicate) { setErrorMsg('Ya existe una marca con el mismo código y nombre.'); return; }

    if (isEditExisting) {
      onUpdate(editing.id, { name, status, materialType });
      setStatusMsg('Marca-item actualizada correctamente');
    } else {
      onAdd({ name, status, materialType });
      setStatusMsg('Marca-item creada correctamente');
    }
    closeModal();
    setTimeout(() => setStatusMsg(null), 3000);
  };

  const statusBadge = (status) => {
    const base = 'px-2 py-1 rounded-full text-xs font-medium';
    const cls = status === 'Activo' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-700 border border-gray-200';
    return <span className={`${base} ${cls}`}>{status}</span>;
  };

  const materialNameMap = useMemo(() => {
    const map = new Map();
    (materials || []).forEach(m => map.set(m.code, m.name));
    return map;
  }, [materials]);

  const exportToXlsx = () => {
    try {
      const data = (filtered || []).map((b) => ({
        'Código Material': b.materialCode || '',
        'Nombre Material': materialNameMap.get(b.materialCode) || '',
        'Marca': b.name || '',
        'Estado': b.status || '',
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Marcas');
      const filename = `marcas_${new Date().toISOString().slice(0,10)}.xlsx`;
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'almacen', reportName: 'Marcas por material' });
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error('Error exportando marcas a .xlsx', err);
      alert('No se pudo exportar el archivo .xlsx.');
    }
  };

  return (
    <div>
      {statusMsg && (
        <div className="mb-3 p-2 text-sm rounded border bg-green-50 text-green-700 border-green-200">{statusMsg}</div>
      )}
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-title">Gestión de Marca-item</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportToXlsx}
            className="inline-flex items-center gap-2 text-white px-3 py-2 rounded-md text-sm"
            style={{ backgroundColor: '#04873B' }}
          >
            <Download size={18} /> Exportar .xlsx
          </button>
          <button
            type="button"
            onClick={openNew}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md text-sm"
          >
            <Plus size={18} /> Nueva Marca-item
          </button>
        </div>
      </div>

      <SearchBar
        value={query}
        onChange={setQuery}
        placeholder="Buscar por nombre o código de material..."
      />

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-700">
              <th className="px-4 py-2 text-left">Marca</th>
              <th className="px-4 py-2 text-left">Estado</th>
              <th className="px-4 py-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id} className="border-t">
                <td className="px-4 py-2">{b.name}</td>
                <td className="px-4 py-2">{statusBadge(b.status)}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-700"
                      onClick={() => openEdit(b)}
                      aria-label="Editar"
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => onDelete(b.id)}
                      aria-label="Eliminar"
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-gray-500">No se encontraron marcas.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} title={editing ? 'Editar Marca-item' : 'Nueva Marca-item'}>
        {editing ? (
          <form
            onSubmit={(e) => { e.preventDefault(); handleSave(editData); }}
            className="grid grid-cols-2 gap-4"
          >
            {errorMsg && (
              <div className="col-span-2 text-red-600 text-sm mb-2">{errorMsg}</div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Código de Material *</label>
              <select
                value={editData.materialCode}
                onChange={(e) => setEditData({ ...editData, materialCode: e.target.value })}
                className={`w-full border rounded px-3 py-2 ${errorMsg.includes('código') ? 'border-red-500' : ''}`}
                required
              >
                <option value="">Seleccione...</option>
                {materials.map((m) => (
                  <option key={m.id} value={m.code}>{m.code ? `${m.code} - ${m.name}` : `${m.id} - ${m.name}`}</option>
                ))}
              </select>
              {errorMsg.includes('código') && (
                <p className="text-xs text-red-600 mt-1">Verifique el código de material.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nombre *</label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className={`w-full border rounded px-3 py-2 ${errorMsg.includes('nombre') ? 'border-red-500' : ''}`}
                required
              />
              {errorMsg.includes('nombre') && (
                <p className="text-xs text-red-600 mt-1">Ingrese el nombre de la marca.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado *</label>
              <select
                value={editData.status}
                onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option>Activo</option>
                <option>Inactivo</option>
              </select>
            </div>
            <div className="col-span-2 flex gap-2 mt-2">
              <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">Guardar</button>
              <button type="button" onClick={closeModal} className="bg-gray-300 px-4 py-2 rounded">Cancelar</button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); handleSave(newData); }}
            className="grid grid-cols-2 gap-4"
          >
            {errorMsg && (
              <div className="col-span-2 text-red-600 text-sm mb-2">{errorMsg}</div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Tipo de Material *</label>
              <select
                value={newData.materialType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  const currentCode = (newData.materialCode || '').trim();
                  const isStillValid = materials.some(m => (
                    (m.code || '').trim() === currentCode && (m.type || '') === nextType
                  ));
                  setNewData({
                    ...newData,
                    materialType: nextType,
                    materialCode: isStillValid ? currentCode : ''
                  });
                }}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option>Material</option>
                <option>Herramientas</option>
                <option>Tecnologia</option>
                <option>Dotacion</option>
                <option>Servicios-Generales</option>
              </select>
            </div>
            {/* Campo de material eliminado: la marca ahora se configura por tipo */}
            <div>
              <label className="block text-sm font-medium mb-1">Nombre *</label>
              <input
                type="text"
                value={newData.name}
                onChange={(e) => setNewData({ ...newData, name: e.target.value })}
                className={`w-full border rounded px-3 py-2 ${errorMsg.includes('nombre') ? 'border-red-500' : ''}`}
                required
              />
              {errorMsg.includes('nombre') && (
                <p className="text-xs text-red-600 mt-1">Ingrese el nombre de la marca.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado *</label>
              <select
                value={newData.status}
                onChange={(e) => setNewData({ ...newData, status: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option>Activo</option>
                <option>Inactivo</option>
              </select>
            </div>
            <div className="col-span-2 flex gap-2 mt-2">
              <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">Guardar</button>
              <button type="button" onClick={closeModal} className="bg-gray-300 px-4 py-2 rounded">Cancelar</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}